// src/components/Pages/ChildDetails.js
import React, { useEffect, useMemo, useState, useCallback } from "react";
import toast from "react-hot-toast";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  Calendar,
  MapPin,
  User,
  Phone,
  Heart,
  FileText,
  LineChart,
  Pencil,
  Save,
  Award,
  Home,
} from "lucide-react";
import Button from "../UI/Button";
import EditProfileModal from "../Modals/EditProfileModal";
import ReintegrationDetailsModal from "../Modals/ReintegrationDetailsModal";
import DevelopmentSummaryModal from "../Modals/DevelopmentSummaryModal";

/* ===========================
   Helpers
   =========================== */
const getInitials = (name = "") => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "";
  const first = parts[0]?.[0] || "";
  const last = parts.length > 1 ? parts[parts.length - 1]?.[0] || "" : "";
  return (first + last).toUpperCase();
};

const isValidPhoto = (v) => {
  if (!v) return false;
  const s = String(v).trim();
  if (!s || s === "null" || s === "undefined") return false;
  if (s.includes("pravatar.cc")) return false;
  return true;
};

const ChildAvatar = ({ fullName, photoUrl, size = 72 }) => {
  const initials = getInitials(fullName);
  const [broken, setBroken] = useState(false);

  useEffect(() => setBroken(false), [photoUrl]);

  const canShowPhoto = isValidPhoto(photoUrl) && !broken;

  if (canShowPhoto) {
    return (
      <img
        src={photoUrl}
        alt={fullName}
        className="rounded-full object-cover border border-gray-200 dark:border-gray-800"
        style={{ width: size, height: size }}
        onError={() => setBroken(true)}
        referrerPolicy="no-referrer"
      />
    );
  }

  return (
    <div
      className="rounded-full flex items-center justify-center
                 border border-gray-200 dark:border-gray-800
                 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 font-semibold"
      style={{ width: size, height: size }}
      title={fullName}
    >
      {initials || <User size={22} />}
    </div>
  );
};

const getStatusColor = (status) => {
  switch (status) {
    case "Active":
      return "bg-green-100 text-green-800 border-green-200 dark:bg-green-950/40 dark:text-green-200 dark:border-green-900";
    case "Transitioning":
      return "bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-950/40 dark:text-yellow-200 dark:border-yellow-900";
    case "Transferred":
      return "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-950/40 dark:text-blue-200 dark:border-blue-900";
    case "Reintegrated":
      return "bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-950/40 dark:text-purple-200 dark:border-purple-900";
    default:
      return "bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700";
  }
};

const getHealthStatusColor = (status) => {
  switch (status) {
    case "Excellent":
      return "bg-green-100 text-green-800 border-green-200 dark:bg-green-950/40 dark:text-green-200 dark:border-green-900";
    case "Good":
      return "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-950/40 dark:text-blue-200 dark:border-blue-900";
    case "Needs Check-up":
      return "bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-950/40 dark:text-orange-200 dark:border-orange-900";
    case "Requires Attention":
      return "bg-red-100 text-red-800 border-red-200 dark:bg-red-950/40 dark:text-red-200 dark:border-red-900";
    default:
      return "bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700";
  }
};

const getAdoptionStatusColor = (status) => {
  switch (status) {
    case "Open for Adoption":
      return "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-950/40 dark:text-blue-200 dark:border-blue-900";
    case "Adopted":
      return "bg-green-100 text-green-800 border-green-200 dark:bg-green-950/40 dark:text-green-200 dark:border-green-900";
    case "Not Available for Adoption":
      return "bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700";
    default:
      return "bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700";
  }
};

const pad2 = (n) => String(n).padStart(2, "0");

const toDateInputValue = (value) => {
  if (!value) return "";
  try {
    if (value instanceof Date) {
      return `${value.getFullYear()}-${pad2(value.getMonth() + 1)}-${pad2(value.getDate())}`;
    }

    const s = String(value).trim();
    if (!s) return "";

    if (s.includes("-") && s.split("-").length >= 3) {
      const [yy, mm, dd] = s.split("-");
      if (yy.length === 4) return `${yy}-${pad2(mm)}-${pad2(dd)}`;
    }

    if (s.includes("/") && s.split("/").length === 3) {
      const [m, d, y] = s.split("/");
      if (String(y).length === 4) return `${y}-${pad2(m)}-${pad2(d)}`;
    }

    const dt = new Date(s);
    if (Number.isNaN(dt.getTime())) return "";
    return `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`;
  } catch {
    return "";
  }
};

const formatShortDate = (value) => {
  if (!value) return "—";

  const iso = toDateInputValue(value);
  if (!iso) return String(value);

  const [year, month, day] = iso.split("-");

  return `${month}/${day}/${year}`;
};

const calculateAge = (dateOfBirth) => {
  const iso = toDateInputValue(dateOfBirth);
  if (!iso) return "";

  const birth = new Date(iso);
  if (Number.isNaN(birth.getTime())) return "";

  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }

  return age >= 0 ? age : "";
};

const FieldLabel = ({ children }) => (
  <label className="text-sm font-medium text-gray-800 dark:text-gray-200">
    {children}
  </label>
);

const TextInput = ({ className = "", ...props }) => (
  <input
    {...props}
    className={[
      "w-full mt-2 px-4 py-3 rounded-xl bg-gray-100 dark:bg-gray-800",
      "border border-transparent focus:border-blue-400 focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-900/40",
      "outline-none text-gray-900 dark:text-gray-100 placeholder:text-gray-500 dark:placeholder:text-gray-400",
      className,
    ].join(" ")}
  />
);

const SelectInput = ({ className = "", ...props }) => (
  <select
    {...props}
    className={[
      "w-full mt-2 px-4 py-3 rounded-xl bg-gray-100 dark:bg-gray-800",
      "border border-transparent focus:border-blue-400 focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-900/40",
      "outline-none text-gray-900 dark:text-gray-100",
      className,
    ].join(" ")}
  />
);

const TextArea = ({ className = "", ...props }) => (
  <textarea
    {...props}
    className={[
      "w-full mt-2 px-4 py-3 rounded-xl bg-gray-100 dark:bg-gray-800",
      "border border-transparent focus:border-blue-400 focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-900/40",
      "outline-none text-gray-900 dark:text-gray-100 placeholder:text-gray-500 dark:placeholder:text-gray-400",
      "min-h-[120px] resize-y",
      className,
    ].join(" ")}
  />
);

const HEALTH_RECORD_TYPES = [
  "Routine Check-up",
  "Vaccination",
  "Dental Check-up",
  "Lab Test",
  "Medication",
  "Specialist Visit",
  "Other",
];

const HONOR_OPTIONS = [
  "None",
  "With Honors",
  "High Honors",
  "Magna Cum Laude (High Honors)",
  "Summa Cum Laude",
  "Outstanding Student",
];

const HealthRecordFormCard = ({
  mode = "add",
  initial,
  onSubmit,
  saving,
  error,
  childName,
  onCancel,
}) => {
  const [type, setType] = useState(initial?.type || "");
  const [provider, setProvider] = useState(initial?.provider || "");
  const [date, setDate] = useState(toDateInputValue(initial?.date) || "");
  const [notes, setNotes] = useState(initial?.notes || "");
  const [nextAppointment, setNextAppointment] = useState(
    toDateInputValue(initial?.nextAppointment) || ""
  );

  useEffect(() => {
    setType(initial?.type || "");
    setProvider(initial?.provider || "");
    setDate(toDateInputValue(initial?.date) || "");
    setNotes(initial?.notes || "");
    setNextAppointment(toDateInputValue(initial?.nextAppointment) || "");
  }, [initial]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      ...initial,
      type: type.trim(),
      provider: provider.trim(),
      date: date || "",
      notes: notes.trim(),
      nextAppointment: nextAppointment || "",
    };
    if (!payload.type || !payload.provider || !payload.date || !payload.notes) return;
    onSubmit?.(payload);
  };

  return (
    <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 md:p-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
        {mode === "edit" ? "Edit Health Record" : "Add Health Record"}
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{childName}</p>

      {error ? (
        <div className="mt-4 p-4 rounded-xl border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-950/20 text-red-800 dark:text-red-200">
          {error}
        </div>
      ) : null}

      <form onSubmit={handleSubmit} className="space-y-6 mt-5">
        <div>
          <FieldLabel>Record Type</FieldLabel>
          <SelectInput
            value={type}
            onChange={(e) => setType(e.target.value)}
            required
            disabled={saving}
          >
            <option value="" disabled>
              Select record type
            </option>
            {HEALTH_RECORD_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </SelectInput>
        </div>

        <div>
          <FieldLabel>Doctor/Healthcare Provider</FieldLabel>
          <TextInput
            value={provider}
            onChange={(e) => setProvider(e.target.value)}
            placeholder="e.g. Dr. Sarah Wilson"
            required
            disabled={saving}
          />
        </div>

        <div>
          <FieldLabel>Date</FieldLabel>
          <TextInput
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            required
            disabled={saving}
          />
        </div>

        <div>
          <FieldLabel>Findings/Notes</FieldLabel>
          <TextArea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Enter examination findings, diagnoses, treatments, or notes"
            required
            disabled={saving}
          />
        </div>

        <div>
          <FieldLabel>Next Appointment (Optional)</FieldLabel>
          <TextInput
            type="date"
            value={nextAppointment}
            onChange={(e) => setNextAppointment(e.target.value)}
            disabled={saving}
          />
        </div>

        <div className="flex items-center justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            size="medium"
            onClick={onCancel}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="medium"
            className="flex items-center gap-2"
            disabled={saving}
          >
            <Save size={16} />
            {saving ? "Saving..." : mode === "edit" ? "Save Changes" : "Add Record"}
          </Button>
        </div>
      </form>
    </div>
  );
};

const EducationLevelFormCard = ({
  mode = "add",
  initial,
  onSubmit,
  saving,
  error,
  childName,
  onCancel,
}) => {
  const [educationLevel, setEducationLevel] = useState(initial?.educationLevel || "");
  const [school, setSchool] = useState(initial?.school || "");
  const [finalAverage, setFinalAverage] = useState(String(initial?.finalAverage ?? ""));
  const [honor, setHonor] = useState(initial?.honor || "None");

  useEffect(() => {
    setEducationLevel(initial?.educationLevel || "");
    setSchool(initial?.school || "");
    setFinalAverage(String(initial?.finalAverage ?? ""));
    setHonor(initial?.honor || "None");
  }, [initial]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const avg = finalAverage === "" ? "" : Number(finalAverage);
    if (finalAverage !== "" && Number.isNaN(avg)) return;

    onSubmit?.({
      ...initial,
      educationLevel: educationLevel.trim(),
      school: school.trim(),
      finalAverage: finalAverage === "" ? "" : avg,
      honor,
    });
  };

  return (
    <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 md:p-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
        {mode === "edit" ? "Edit Education Record" : "Add Education Record"}
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{childName}</p>

      {error ? (
        <div className="mt-4 p-4 rounded-xl border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-950/20 text-red-800 dark:text-red-200">
          {error}
        </div>
      ) : null}

      <form onSubmit={handleSubmit} className="space-y-6 mt-5">
        <div>
          <FieldLabel>Education Level</FieldLabel>
          <TextInput
            value={educationLevel}
            onChange={(e) => setEducationLevel(e.target.value)}
            placeholder="e.g. Grade 6, Junior High, Senior High, College"
            required
            disabled={saving}
          />
        </div>

        <div>
          <FieldLabel>School</FieldLabel>
          <TextInput
            value={school}
            onChange={(e) => setSchool(e.target.value)}
            placeholder="School name"
            required
            disabled={saving}
          />
        </div>

        <div>
          <FieldLabel>Final Average (%)</FieldLabel>
          <TextInput
            type="number"
            step="0.01"
            value={finalAverage}
            onChange={(e) => setFinalAverage(e.target.value)}
            placeholder="e.g. 91.00"
            disabled={saving}
          />
        </div>

        <div>
          <FieldLabel>Honor/Recognition</FieldLabel>
          <SelectInput value={honor} onChange={(e) => setHonor(e.target.value)} disabled={saving}>
            {HONOR_OPTIONS.map((h) => (
              <option key={h} value={h}>
                {h}
              </option>
            ))}
          </SelectInput>
        </div>

        <div className="flex items-center justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            size="medium"
            onClick={onCancel}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="medium"
            className="flex items-center gap-2"
            disabled={saving}
          >
            <Save size={16} />
            {saving ? "Saving..." : mode === "edit" ? "Save Changes" : "Add Record"}
          </Button>
        </div>
      </form>
    </div>
  );
};

const ChildDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:5000";
  const API_URL = `${API_BASE}/api/children`;
  const token = localStorage.getItem("admin_token");

  const [child, setChild] = useState(location.state?.child || null);
  const [loading, setLoading] = useState(!location.state?.child);

  const [activeTab, setActiveTab] = useState("overview");

  const [showEdit, setShowEdit] = useState(false);
  const [showDevelopment, setShowDevelopment] = useState(false);
  const [showReintegrationDetails, setShowReintegrationDetails] = useState(false);

  const [healthRecords, setHealthRecords] = useState([]);
  const [healthLoading, setHealthLoading] = useState(false);
  const [healthError, setHealthError] = useState("");
  const [healthSaving, setHealthSaving] = useState(false);
  const [showHealthForm, setShowHealthForm] = useState(false);
  const [healthEditing, setHealthEditing] = useState(null);

  const [educationLevels, setEducationLevels] = useState([]);
  const [eduLoading, setEduLoading] = useState(false);
  const [eduError, setEduError] = useState("");
  const [eduSaving, setEduSaving] = useState(false);
  const [showEduForm, setShowEduForm] = useState(false);
  const [eduEditingLevel, setEduEditingLevel] = useState(null);

  const jsonHeaders = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    }),
    [token]
  );

  const normalizePhoto = useCallback(
    (c) => {
      const raw = c?.photoUrl ?? c?.photo_url ?? c?.photo ?? null;
      if (!raw) return null;

      const s = String(raw).trim();
      if (!s || s === "null" || s === "undefined") return null;
      if (s.includes("pravatar.cc")) return null;

      if (/^https?:\/\//i.test(s)) return s;
      if (s.startsWith("/uploads/")) return `${API_BASE}${s}`;
      if (s.startsWith("uploads/")) return `${API_BASE}/${s}`;
      return `${API_BASE}/uploads/${s.replace(/^\/+/, "")}`;
    },
    [API_BASE]
  );

  const withNormalizedChild = useCallback(
    (c) => ({
      ...c,
      photoUrl: normalizePhoto(c),
      reintegration: c.reintegration || c.reintegration_details || null,
    }),
    [normalizePhoto]
  );

  const fullName = useMemo(() => {
    const safe = child || {};
    const firstName = safe.firstName ?? safe.first_name ?? "";
    const middleName = safe.middleName ?? safe.middle_name ?? "";
    const lastName = safe.lastName ?? safe.last_name ?? "";
    return `${firstName} ${middleName ? middleName + " " : ""}${lastName}`.trim();
  }, [child]);

  const dateOfBirth = child?.dateOfBirth ?? child?.date_of_birth ?? "";
  const computedAge = calculateAge(dateOfBirth);

  const status = child?.status ?? "";
  const healthStatus = child?.healthStatus ?? child?.health_status ?? "";
  const adoptionStatus = child?.adoptionStatus ?? child?.adoption_status ?? "";
  const educationLevel = child?.educationLevel ?? child?.education_level ?? "";

  useEffect(() => {
    const fetchChild = async () => {
      if (!id || !token) return;

      setLoading(true);

      try {
        const res = await fetch(`${API_URL}/${id}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        });

        const data = await res.json().catch(() => ({}));

        if (res.ok && data?.success !== false) {
          const record = data.child || data;
          if (record && (record.id || record.firstName || record.first_name)) {
            setChild(withNormalizedChild(record));
            setLoading(false);
            return;
          }
        }

        // fallback: fetch all children then find the matching one
        const listRes = await fetch(API_URL, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        });

        const listData = await listRes.json().catch(() => ({}));
        if (!listRes.ok || listData?.success === false) {
          throw new Error(listData?.message || listData?.error || "Failed to load child.");
        }

        const list = Array.isArray(listData) ? listData : listData.children || [];
        const found = list.find((c) => String(c.id) === String(id));

        if (!found) {
          throw new Error("Child record not found.");
        }

        setChild(withNormalizedChild(found));
      } catch (err) {
        if (!location.state?.child) {
          toast.error(err.message || "Failed to load child.");
          setChild(null);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchChild();
  }, [id, token, API_URL, withNormalizedChild, location.state]);

  const fallbackEducationLevels = useMemo(() => {
    const safeChild = child || {};
    const summary = safeChild.educationSummary ?? safeChild.education_summary ?? {
      school: "",
      averageGrade: "",
      honor: "None",
    };

    const school = summary.school ?? summary.level ?? safeChild.school ?? "";
    const avg = summary.averageGrade ?? summary.average_grade ?? "";
    const honor = summary.honor ?? summary.recognition ?? "None";
    const level =
      safeChild.educationLevel ??
      safeChild.education_level ??
      summary.educationLevel ??
      summary.education_level ??
      "";

    return [
      {
        id: "summary",
        educationLevel: level || "—",
        school: school || "",
        finalAverage: avg === "" ? "" : avg,
        honor: honor || "None",
      },
    ];
  }, [child]);

  const fetchHealthRecords = async () => {
    if (!child?.id) return;
    setHealthLoading(true);
    setHealthError("");

    try {
      const res = await fetch(`${API_URL}/${child.id}/health-records`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) {
        throw new Error(data?.error || "Failed to fetch health records");
      }

      const mapped = (data.records || []).map((r) => ({
        id: r.id,
        type: r.recordType || r.record_type || "",
        provider: r.provider || "",
        date: r.recordDate || r.record_date || "",
        notes: r.notes || "",
        nextAppointment: r.nextAppointment || r.next_appointment || "",
      }));

      setHealthRecords(mapped);
    } catch (e) {
      setHealthError(e.message || "Failed to fetch health records");
    } finally {
      setHealthLoading(false);
    }
  };

const fetchEducation = async () => {
  if (!child?.id) return;

  setEduLoading(true);
  setEduError("");

  try {
    const res = await fetch(`${API_URL}/${child.id}/education`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok || !data?.success) {
      throw new Error(data?.error || data?.message || "Failed to fetch education records");
    }

    if (Array.isArray(data.levels) && data.levels.length > 0) {
      setEducationLevels(
        data.levels.map((r) => ({
          id: r.id,
          educationLevel: r.educationLevel || "",
          school: r.school || "",
          finalAverage:
            r.finalAverage === "" || r.finalAverage === null || r.finalAverage === undefined
              ? ""
              : Number(r.finalAverage),
          honor: r.honor || "None",
        }))
      );
    } else {
      setEducationLevels(fallbackEducationLevels);
    }
  } catch (err) {
    setEduError(err.message || "Failed to fetch education records");
    setEducationLevels(fallbackEducationLevels);
  } finally {
    setEduLoading(false);
  }
};

  useEffect(() => {
    if (child?.id) {
      fetchHealthRecords();
      fetchEducation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [child?.id]);

  const sortedHealthRecords = useMemo(() => {
    const toTime = (d) => {
      const iso = toDateInputValue(d);
      if (!iso) return 0;
      const t = new Date(iso).getTime();
      return Number.isNaN(t) ? 0 : t;
    };
    return [...healthRecords].sort((a, b) => toTime(b.date) - toTime(a.date));
  }, [healthRecords]);

  const updateChild = async (updatedChild) => {
    const job = async () => {
      const childId = updatedChild?.id;
      if (!childId) throw new Error("Missing child ID.");

      const formData = new FormData();

      Object.entries(updatedChild || {}).forEach(([key, value]) => {
        if (value === undefined || value === null) return;
        if (key === "photo") return;
        if (key === "photoUrl") return;
        if (key === "reintegration") return;
        formData.append(key, value);
      });

      if (updatedChild?.photo instanceof File) {
        formData.append("photo", updatedChild.photo);
      }

      const res = await fetch(`${API_URL}/${childId}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
        body: formData,
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.success === false) {
        throw new Error(data?.message || data?.error || "Failed to update child.");
      }

      const saved = data.child || data;
      setChild(withNormalizedChild(saved));
      setShowEdit(false);
    };

    try {
      await toast.promise(job(), {
        loading: "Saving changes...",
        success: "Child profile updated successfully!",
        error: (e) => e?.message || "Failed to update child.",
      });
    } catch (err) {
      console.error("Update child error:", err);
    }
  };

  const handleAddHealth = async (payload) => {
    if (!child?.id) return;
    setHealthSaving(true);
    setHealthError("");

    try {
      const res = await fetch(`${API_URL}/${child.id}/health-records`, {
        method: "POST",
        headers: jsonHeaders,
        body: JSON.stringify({
          recordType: payload.type,
          provider: payload.provider,
          recordDate: payload.date,
          notes: payload.notes,
          nextAppointment: payload.nextAppointment || null,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) {
        throw new Error(data?.error || "Failed to add health record");
      }

      await fetchHealthRecords();
      setShowHealthForm(false);
      setHealthEditing(null);
      toast.success("Health record added");
    } catch (e) {
      setHealthError(e.message || "Failed to add health record");
      toast.error(e.message || "Failed to add health record");
    } finally {
      setHealthSaving(false);
    }
  };

  const handleEditHealth = async (payload) => {
    if (!payload?.id) return;
    setHealthSaving(true);
    setHealthError("");

    try {
      const res = await fetch(`${API_URL}/health-records/${payload.id}`, {
        method: "PUT",
        headers: jsonHeaders,
        body: JSON.stringify({
          recordType: payload.type,
          provider: payload.provider,
          recordDate: payload.date,
          notes: payload.notes,
          nextAppointment: payload.nextAppointment || null,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) {
        throw new Error(data?.error || "Failed to update health record");
      }

      await fetchHealthRecords();
      setShowHealthForm(false);
      setHealthEditing(null);
      toast.success("Health record updated");
    } catch (e) {
      setHealthError(e.message || "Failed to update health record");
      toast.error(e.message || "Failed to update health record");
    } finally {
      setHealthSaving(false);
    }
  };

const handleUpsertEducationLevel = async (payload) => {
  if (!child?.id) return;

  setEduSaving(true);
  setEduError("");
  toast.loading("Saving education record...", { id: "edu-save" });

  const body = {
    educationLevel: String(payload?.educationLevel || "").trim(),
    school: String(payload?.school || "").trim(),
    finalAverage:
      payload?.finalAverage === "" ||
      payload?.finalAverage === null ||
      payload?.finalAverage === undefined
        ? null
        : Number(payload.finalAverage),
    honor: String(payload?.honor || "None").trim() || "None",
  };

  try {
    if (!body.educationLevel) {
      throw new Error("Education level is required");
    }

    if (body.finalAverage !== null && Number.isNaN(body.finalAverage)) {
      throw new Error("Final average must be a valid number");
    }

    let res;
    let data;
    const isEdit = !!payload?.id && payload.id !== "summary";

    if (isEdit) {
      res = await fetch(`${API_URL}/education-levels/${payload.id}`, {
        method: "PUT",
        headers: jsonHeaders,
        body: JSON.stringify(body),
      });
    } else {
      res = await fetch(`${API_URL}/${child.id}/education-levels`, {
        method: "POST",
        headers: jsonHeaders,
        body: JSON.stringify(body),
      });
    }

    data = await res.json().catch(() => ({}));

    if (!res.ok || !data?.success) {
      throw new Error(data?.error || data?.message || "Failed to save education record");
    }

    await fetchEducation();

    setChild((prev) =>
      prev
        ? {
            ...prev,
            educationLevel: body.educationLevel,
            education_level: body.educationLevel,
          }
        : prev
    );

    setShowEduForm(false);
    setEduEditingLevel(null);
    toast.success("Education record saved", { id: "edu-save" });
  } catch (e) {
    setEduError(e.message || "Failed to save education record");
    toast.error(e.message || "Failed to save education record", { id: "edu-save" });
  } finally {
    setEduSaving(false);
  }
};

  if (loading) {
    return (
      <div className="p-6 md:p-8">
        <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6">
          <p className="text-gray-700 dark:text-gray-300">Loading child details...</p>
        </div>
      </div>
    );
  }

  if (!child) {
    return (
      <div className="p-6 md:p-8">
        <div className="rounded-2xl border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-950/20 p-6">
          <p className="text-red-800 dark:text-red-200">Child record not found.</p>
          <div className="mt-4">
            <Button type="button" variant="outline" onClick={() => navigate("/children")}>
              Back to Children
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const isReintegrated =
    String(status).toLowerCase() === "reintegrated" || !!child.reintegration;

  const reintegrationName =
    child?.reintegration?.guardianName ||
    child?.reintegration?.reintegratedWith ||
    child?.reintegration?.parentName ||
    "";

  return (
    <div className="p-6 md:p-8 space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <button
            type="button"
            onClick={() => navigate("/children")}
            className="inline-flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 hover:underline mb-3"
          >
            <ArrowLeft size={16} />
            Back to Children
          </button>

          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            Child Details
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            View child profile, health, education, and reintegration details
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
        <button
            type="button"
            onClick={() => setShowDevelopment(true)}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl
                    bg-blue-600 text-white font-medium
                    hover:bg-blue-700 transition-all duration-200
                    shadow-sm hover:shadow-md"
        >
            <LineChart size={18} />
            View Development
        </button>

        <button
            type="button"
            onClick={() => setShowEdit(true)}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl
                    border border-gray-300 dark:border-gray-700
                    text-gray-800 dark:text-gray-200 font-medium
                    hover:bg-gray-100 dark:hover:bg-gray-800
                    transition-all duration-200"
        >
            <Pencil size={18} />
            Edit Profile
        </button>
        </div>
      </div>

      <div className="rounded-3xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm overflow-hidden">
        <div className="p-6 md:p-8 border-b border-gray-200 dark:border-gray-800">
          <div className="flex flex-col md:flex-row md:items-start gap-5">
            <ChildAvatar fullName={fullName} photoUrl={child.photoUrl} size={84} />

            <div className="min-w-0 flex-1">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {fullName || "Unnamed Child"}
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                {computedAge !== "" ? `${computedAge} years old` : "Age unavailable"} •{" "}
                {child.gender || "—"}
              </p>

              <div className="flex flex-wrap gap-2 mt-4">
                <span className={`px-3 py-1 rounded-full border text-sm ${getStatusColor(status)}`}>
                  {status || "—"}
                </span>
                <span
                  className={`px-3 py-1 rounded-full border text-sm ${getHealthStatusColor(
                    healthStatus
                  )}`}
                >
                  {healthStatus || "—"}
                </span>
                <span
                  className={`px-3 py-1 rounded-full border text-sm ${getAdoptionStatusColor(
                    adoptionStatus
                  )}`}
                >
                  {adoptionStatus || "—"}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 md:px-8 pt-5">
          <div className="flex flex-wrap gap-2 border-b border-gray-200 dark:border-gray-800 pb-4">
            {["overview", "health", "education"].map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-200
                ${
                    activeTab === tab
                    ? "bg-blue-600 text-white shadow-sm"
                    : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700"
                }`}
              >
                {tab === "overview"
                  ? "Overview"
                  : tab === "health"
                  ? "Health Records"
                  : "Education Records"}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6 md:p-8">
          {activeTab === "overview" && (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950/30 p-5">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  Basic Information
                </h3>
                <div className="space-y-3 text-gray-700 dark:text-gray-300">
                  <p className="flex items-center gap-3">
                    <Calendar size={18} className="text-gray-400" />
                    <span>
                      Date of Birth: {formatShortDate(child.dateOfBirth ?? child.date_of_birth)}
                    </span>
                  </p>
                  <p className="flex items-center gap-3">
                    <Calendar size={18} className="text-gray-400" />
                    <span>
                      Admission Date:{" "}
                      {formatShortDate(child.admissionDate ?? child.admission_date)}
                    </span>
                  </p>
                  <p className="flex items-center gap-3">
                    <Home size={18} className="text-gray-400" />
                    <span>House: {child.house || "—"}</span>
                  </p>
                  <p className="flex items-center gap-3">
                    <User size={18} className="text-gray-400" />
                    <span>House Parent: {child.houseParent || child.house_parent || "—"}</span>
                  </p>
                  <p className="flex items-center gap-3">
                    <MapPin size={18} className="text-gray-400" />
                    <span>Address: {child.address || "—"}</span>
                  </p>
                  <p className="flex items-center gap-3">
                    <Phone size={18} className="text-gray-400" />
                    <span>
                      Emergency: {child.emergencyContact || child.emergency_contact || "—"}
                    </span>
                  </p>
                </div>
              </div>

              <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950/30 p-5">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  Health & Education
                </h3>
                <div className="space-y-3 text-gray-700 dark:text-gray-300">
                  <p className="flex items-center gap-3">
                    <Heart size={18} className="text-gray-400" />
                    <span>Health Status: {healthStatus || "—"}</span>
                  </p>
                  <p className="flex items-center gap-3">
                    <Calendar size={18} className="text-gray-400" />
                    <span>
                      Last Check-up: {formatShortDate(child.lastCheckup || child.last_checkup)}
                    </span>
                  </p>
                  <p className="flex items-center gap-3">
                    <FileText size={18} className="text-gray-400" />
                    <span>Education Level: {educationLevel || "—"}</span>
                  </p>
                </div>
              </div>

              <div className="xl:col-span-2 rounded-2xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950/30 p-5">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  Notes
                </h3>
                <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 min-h-[90px] text-gray-700 dark:text-gray-300">
                  {child.notes || "No notes available."}
                </div>
              </div>

              {isReintegrated && (
                <div className="xl:col-span-2 rounded-2xl border border-green-200 dark:border-green-900/40 bg-green-50 dark:bg-green-950/20 p-5">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                      <h3 className="text-xl font-semibold text-green-800 dark:text-green-200">
                        Reintegration
                      </h3>
                      <p className="text-green-700 dark:text-green-300 mt-1">
                        Reintegrated with {reintegrationName || "guardian/family"}.
                      </p>
                    </div>

                    {child.reintegration ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="medium"
                        onClick={() => setShowReintegrationDetails(true)}
                      >
                        <Home size={16} className="mr-2" />
                        View Reintegration Details
                      </Button>
                    ) : null}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === "health" && (
            <div className="space-y-5">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                    Health Records
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{fullName}</p>
                </div>

                <Button
                  type="button"
                  variant="primary"
                  size="medium"
                  onClick={() => {
                    setHealthError("");
                    setHealthEditing(null);
                    setShowHealthForm(true);
                  }}
                >
                  Add Health Record
                </Button>
              </div>

              {showHealthForm && (
                <HealthRecordFormCard
                  mode={healthEditing ? "edit" : "add"}
                  initial={
                    healthEditing || {
                      type: "",
                      provider: "",
                      date: new Date(),
                      notes: "",
                      nextAppointment: "",
                    }
                  }
                  onSubmit={healthEditing ? handleEditHealth : handleAddHealth}
                  saving={healthSaving}
                  error={healthError}
                  childName={fullName}
                  onCancel={() => {
                    setShowHealthForm(false);
                    setHealthEditing(null);
                  }}
                />
              )}

              {healthLoading ? (
                <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6">
                  <p className="text-gray-700 dark:text-gray-300">Loading health records...</p>
                </div>
              ) : healthError && !showHealthForm ? (
                <div className="rounded-2xl border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-950/20 p-6 text-red-800 dark:text-red-200">
                  {healthError}
                </div>
              ) : sortedHealthRecords.length ? (
                <div className="space-y-4">
                  {sortedHealthRecords.map((record) => (
                    <div
                      key={record.id}
                      className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                          <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                            {record.type || "Health Record"}
                          </h4>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {record.provider || "—"}
                          </p>
                          <p className="text-sm text-gray-800 dark:text-gray-200 mt-4 whitespace-pre-wrap">
                            {record.notes || "—"}
                          </p>
                          {record.nextAppointment ? (
                            <p className="mt-3 text-sm text-blue-600 dark:text-blue-400">
                              Next Appointment: {formatShortDate(record.nextAppointment)}
                            </p>
                          ) : null}
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-sm px-3 py-1 rounded-xl border border-gray-200 dark:border-gray-700">
                            {formatShortDate(record.date)}
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              setHealthError("");
                              setHealthEditing(record);
                              setShowHealthForm(true);
                            }}
                            className="w-10 h-10 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center justify-center text-gray-700 dark:text-gray-200"
                          >
                            <Pencil size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rounded-2xl border border-dashed border-gray-300 dark:border-gray-700 p-8 text-center">
                  <p className="text-gray-900 dark:text-gray-100 font-semibold">
                    No health records yet
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Click “Add Record” to create the first one.
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === "education" && (
            <div className="space-y-5">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                    Education Records
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{fullName}</p>
                </div>

                <Button
                  type="button"
                  variant="primary"
                  size="medium"
                  onClick={() => {
                    setEduError("");
                    setEduEditingLevel(null);
                    setShowEduForm(true);
                  }}
                >
                  Add Education Record
                </Button>
              </div>

              {showEduForm && (
                <EducationLevelFormCard
                  mode={eduEditingLevel ? "edit" : "add"}
                  initial={
                    eduEditingLevel || {
                      id: null,
                      educationLevel: child.educationLevel || child.education_level || "",
                      school: "",
                      finalAverage: "",
                      honor: "None",
                    }
                  }
                  onSubmit={handleUpsertEducationLevel}
                  saving={eduSaving}
                  error={eduError}
                  childName={fullName}
                  onCancel={() => {
                    setShowEduForm(false);
                    setEduEditingLevel(null);
                  }}
                />
              )}

              {eduLoading ? (
                <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6">
                  <p className="text-gray-700 dark:text-gray-300">Loading education records...</p>
                </div>
              ) : eduError && !showEduForm ? (
                <div className="rounded-2xl border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-950/20 p-6 text-red-800 dark:text-red-200">
                  {eduError}
                </div>
              ) : educationLevels.length ? (
                <div className="space-y-4">
                  {educationLevels.map((record) => {
                    const hasAvg = String(record?.finalAverage ?? "").trim() !== "";
                    return (
                      <div
                        key={record.id}
                        className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="min-w-0">
                            <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                              {record?.educationLevel || "Education Level"}
                            </h4>

                            <div className="mt-2 text-sm text-gray-700 dark:text-gray-300 space-y-2">
                              <div className="flex flex-wrap gap-x-2">
                                <span className="font-medium text-gray-900 dark:text-gray-100">
                                  School:
                                </span>
                                <span>{record?.school || "—"}</span>
                              </div>

                              <div className="flex flex-wrap items-center gap-x-2">
                                <span className="font-medium text-gray-900 dark:text-gray-100">
                                  Final Average:
                                </span>
                                {hasAvg ? (
                                  <span className="text-xs px-2.5 py-1 rounded-full bg-blue-600 text-white">
                                    {Number(record.finalAverage).toFixed(2)}%
                                  </span>
                                ) : (
                                  <span className="text-gray-600 dark:text-gray-400">—</span>
                                )}
                              </div>

                              <div className="flex flex-wrap items-center gap-x-2">
                                <span className="font-medium text-gray-900 dark:text-gray-100">
                                  Honor/Recognition:
                                </span>
                                {record?.honor && record.honor !== "None" ? (
                                  <span className="inline-flex items-center gap-2 text-orange-600 dark:text-orange-400">
                                    <Award size={16} />
                                    <span>{record.honor}</span>
                                  </span>
                                ) : (
                                  <span className="text-gray-600 dark:text-gray-400">None</span>
                                )}
                              </div>
                            </div>
                          </div>

                          <button
                            type="button"
                            onClick={() => {
                              setEduError("");
                              setEduEditingLevel(record);
                              setShowEduForm(true);
                            }}
                            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
                          >
                            <Pencil size={16} />
                            Edit
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="rounded-2xl border border-dashed border-gray-300 dark:border-gray-700 p-8 text-center">
                  <p className="text-gray-900 dark:text-gray-100 font-semibold">
                    No education records yet
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Click “Add Level Record” to create the first one.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showEdit && (
        <EditProfileModal
          child={child}
          onClose={() => setShowEdit(false)}
          onSave={updateChild}
        />
      )}

      {showReintegrationDetails && child?.reintegration && (
        <ReintegrationDetailsModal
          child={child}
          onClose={() => setShowReintegrationDetails(false)}
        />
      )}

      {showDevelopment && (
        <DevelopmentSummaryModal
          child={{ ...child, fullName }}
          token={token}
          onClose={() => setShowDevelopment(false)}
        />
      )}
    </div>
  );
};

export default ChildDetails;